import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Typography,
  Button,
  TextField,
  Box,
  LinearProgress,
  Paper,
  Chip
} from '@mui/material';
import Select from 'react-select';

const BulkFeesModal = ({ 
  open, 
  onClose, 
  shipmentItems, 
  servicesData, 
  updateItemServices,
  showSnackbar,
  reloadShipmentData
}) => {
  const [selected, setSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedServices, setSelectedServices] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalToProcess, setTotalToProcess] = useState(0);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);

  // Reset selected items when modal opens
  useEffect(() => {
    if (open) {
      setSelected([]);
      setSearchTerm('');
      setSelectedServices([]);
      setIsProcessing(false);
      setProgress(0);
      setProcessedCount(0);
      setTotalToProcess(0);
      setCurrentItemIndex(0);
      setCurrentServiceIndex(0);
    }
  }, [open]);

  // Handle selecting/deselecting all items
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const filteredItems = filteredShipmentItems.map(item => item.id);
      setSelected(filteredItems);
      return;
    }
    setSelected([]);
  };

  // Handle selecting/deselecting a single item
  const handleSelectOne = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [...selected];

    if (selectedIndex === -1) {
      newSelected.push(id);
    } else {
      newSelected.splice(selectedIndex, 1);
    }

    setSelected(newSelected);
  };

  // Handle row click to select/deselect
  const handleRowClick = (id) => {
    handleSelectOne(null, id);
  };

  // Filter items based on search term
  const filteredShipmentItems = shipmentItems.filter(item => {
    const searchString = searchTerm.toLowerCase();
    return (
      item.item.title.toLowerCase().includes(searchString) ||
      item.item.asin.toLowerCase().includes(searchString) ||
      item.item.fnsku.toLowerCase().includes(searchString) ||
      item.item.merchant_sku.toLowerCase().includes(searchString)
    );
  });

  // Create service options for react-select
  const serviceOptions = servicesData?.services
    ? [...servicesData.services]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(service => ({
          value: service.name,
          label: service.name,
          service: service
        }))
    : [];

  // Get services that need to be added (avoid duplicates)
  const getServicesToAdd = (item, selectedServiceNames) => {
    // Get existing service names for this item
    const existingServiceNames = item.service_lines?.map(sl => sl.service.name) || [];
    
    // Filter out services that already exist on the item
    return selectedServiceNames.filter(name => !existingServiceNames.includes(name));
  };

  // Apply selected services to all selected items
  const handleApplyService = async () => {
    if (selectedServices.length === 0 || selected.length === 0) {
      showSnackbar('Please select both items and at least one service', 'error');
      return;
    }

    setIsProcessing(true);
    setTotalToProcess(selected.length);
    setProcessedCount(0);
    setProgress(0);
    setCurrentItemIndex(0);

    try {
      // Get the service names
      const serviceNames = selectedServices.map(service => service.value);
      
      // Process each selected item
      for (let i = 0; i < selected.length; i++) {
        setCurrentItemIndex(i);
        const itemId = selected[i];
        const item = shipmentItems.find(item => item.id === itemId);
        
        if (item) {
          // Format item for the updateItemServices function
          const formattedItem = {
            SHIPMENT_ID: item.shipment_id,
            ITEM_ID: item.id,
            SKU: item.item.merchant_sku
          };
          
          // Get services to add (filter out duplicates)
          const servicesToAdd = getServicesToAdd(item, serviceNames);
          
          if (servicesToAdd.length > 0) {
            // Call the function to update services with all selected services
            await updateItemServices(formattedItem, servicesToAdd);
          }
          
          // Update progress
          setProcessedCount(i + 1);
          setProgress(((i + 1) / selected.length) * 100);
        }
      }
      
      // Reload shipment data to reflect the changes
      await reloadShipmentData();
      
      // Show success message
      showSnackbar(`Applied services to ${selected.length} items successfully`, 'success');
      
      // Close modal after processing
      onClose();
    } catch (error) {
      console.error('Error applying services:', error);
      showSnackbar('Failed to apply services to some items', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={!isProcessing ? onClose : undefined}
      className="flex items-center justify-center"
    >
      <Paper className="bg-white rounded-lg shadow-lg max-w-5xl w-full p-6 mx-4 max-h-[90vh] overflow-auto">
        <Typography variant="h5" className="font-bold mb-4 text-center">
          Add Bulk Fees
        </Typography>

        {isProcessing ? (
          <Box className="p-6">
            <Typography variant="h6" className="mb-4 text-center">
              Applying services to {selected.length} items...
            </Typography>
            <Box className="mb-2">
              <LinearProgress variant="determinate" value={progress} />
            </Box>
            <Typography variant="body1" className="text-center">
              {processedCount} of {totalToProcess} items processed
            </Typography>
            <Typography variant="body2" className="text-center mt-2 text-gray-600">
              Processing item {currentItemIndex + 1} of {selected.length}
            </Typography>
          </Box>
        ) : (
          <>
            <Box className="mb-4 flex flex-col gap-4">
              <TextField
                label="Search items"
                variant="outlined"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by title, ASIN, FNSKU, or SKU"
                size="small"
              />
              
              <Box>
                <Typography variant="subtitle2" className="mb-2">
                  Select Services to Apply (Multiple)
                </Typography>
                <Select
                  options={serviceOptions}
                  value={selectedServices}
                  onChange={setSelectedServices}
                  placeholder="Select services..."
                  className="w-full"
                  isMulti
                  isSearchable
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999 // Ensure dropdown is above other elements
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#EFF6FF',
                      borderRadius: '4px',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: '#1E40AF',
                      fontWeight: 500
                    })
                  }}
                />
                <Box className="mt-2 flex flex-wrap gap-1">
                  {selectedServices.map(service => (
                    <Chip 
                      key={service.value}
                      label={service.label}
                      onDelete={() => {
                        setSelectedServices(prev => prev.filter(s => s.value !== service.value));
                      }}
                      color="primary"
                      variant="outlined"
                      size="small"
                      className="m-0.5"
                    />
                  ))}
                </Box>
              </Box>
            </Box>

            <Box sx={{ maxHeight: '50vh', overflow: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        onChange={handleSelectAll}
                        checked={filteredShipmentItems.length > 0 && selected.length === filteredShipmentItems.length}
                        indeterminate={selected.length > 0 && selected.length < filteredShipmentItems.length}
                      />
                    </TableCell>
                    <TableCell>Image</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Existing Services</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredShipmentItems.map((item) => (
                    <TableRow
                      key={item.id}
                      hover
                      onClick={() => handleRowClick(item.id)}
                      selected={selected.includes(item.id)}
                      sx={{ 
                        cursor: 'pointer',
                        bgcolor: selected.includes(item.id) ? 'rgba(25, 118, 210, 0.08)' : 'inherit'
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.includes(item.id)}
                          onChange={(event) => handleSelectOne(event, item.id)}
                          onClick={(event) => event.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>
                        <img
                          src={item.item.images?.[0]?.thumbnail_url}
                          alt={item.item.title}
                          className="w-16 h-16 object-cover"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className="font-semibold">
                          {item.item.title}
                        </Typography>
                        <Typography variant="caption" component="div">
                          ASIN: {item.item.asin}
                        </Typography>
                        <Typography variant="caption" component="div">
                          FNSKU: {item.item.fnsku}
                        </Typography>
                        <Typography variant="caption" component="div">
                          SKU: {item.item.merchant_sku}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>
                        {item.service_lines?.length > 0 ? (
                          <ul className="list-disc ml-5">
                            {item.service_lines.map((service, index) => (
                              <li key={index}>{service.service.name}</li>
                            ))}
                          </ul>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No services
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredShipmentItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No items found matching your search
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>

            <Box className="flex justify-end mt-6 space-x-4">
              <Button variant="outlined" color="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleApplyService}
                disabled={selected.length === 0 || selectedServices.length === 0}
              >
                Apply {selectedServices.length} {selectedServices.length === 1 ? 'Service' : 'Services'} to {selected.length} Selected {selected.length === 1 ? 'Item' : 'Items'}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Modal>
  );
};

export default BulkFeesModal; 